import { WorkflowCanvasType } from "./workflowBuilderData";
import {
  WorkflowInstanceDisplayStatusDto,
  WorkflowInstanceListItemDto,
  WorkflowInstanceListStatusDto,
  WorkflowMyWorkItemDto,
  WorkflowMyWorkKindDto,
} from "../../types/workflowDto";
import { getCurrentESTDate } from "../../utils/dateHelper";

/** The screen's status vocabulary (the prototype's). The API calls in-progress "open". */
export type WorkflowInstanceStatus = "progress" | "done" | "rejected" | "cancelled";
export type WorkflowWorkKind = WorkflowMyWorkKindDto;

export interface WorkflowInstanceListItem {
  id: number;
  definitionId: number;
  definitionName: string;
  refNo: string;
  title: string;
  subtitle: string;
  /** The Run screen's stage key - the engine's stage id as a string. Null once terminal. */
  currentStageId: string | null;
  currentStageName: string;
  currentStageType: WorkflowCanvasType | null;
  status: WorkflowInstanceStatus;
  owner: string;
  age: string;
}

export interface WorkflowMyWorkItem {
  id: string;
  instanceId: number;
  definitionId: number;
  stageId: string;
  stageName: string;
  stageType: WorkflowCanvasType | null;
  caseTitle: string;
  refNo: string;
  kind: WorkflowWorkKind;
  canAct: boolean;
  isOverdue: boolean;
}

export const WORKFLOW_INSTANCE_STATUS_LABELS: Record<WorkflowInstanceStatus, string> = {
  progress: "In progress",
  done: "Completed",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

/** Epic 80431 - the screen's status filter value -> the API's list status. */
export const WORKFLOW_STATUS_FILTER_TO_API: Record<WorkflowInstanceStatus | "all", WorkflowInstanceListStatusDto> = {
  all: "all",
  progress: "open",
  done: "completed",
  rejected: "rejected",
  cancelled: "cancelled",
};

const DISPLAY_STATUS_FROM_API: Record<WorkflowInstanceDisplayStatusDto, WorkflowInstanceStatus> = {
  open: "progress",
  completed: "done",
  rejected: "rejected",
  cancelled: "cancelled",
};

/** Unknown statuses read as in progress rather than being hidden: a case is never dropped. */
export const toWorkflowInstanceStatus = (status: WorkflowInstanceDisplayStatusDto): WorkflowInstanceStatus =>
  DISPLAY_STATUS_FROM_API[status] ?? "progress";

const PLACEHOLDER = "—";

/**
 * A compact age - "now", "12m", "5h", "2d" - from when the case started to now, or to when it
 * finished for a terminal case (a finished case stops ageing). The API sends EST wall-clock
 * times with no offset, so "now" is EST too (dateHelper.formatTimeAgo's rule).
 */
export const formatWorkflowCaseAge = (
  startedOn: string | null,
  completedOn: string | null,
  now: Date = getCurrentESTDate(),
): string => {
  if (!startedOn) return PLACEHOLDER;
  const start = new Date(startedOn).getTime();
  const end = completedOn ? new Date(completedOn).getTime() : now.getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return PLACEHOLDER;

  const minutes = Math.max(0, Math.floor((end - start) / 60000));
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

export const toWorkflowInstanceListItem = (
  dto: WorkflowInstanceListItemDto,
  now?: Date,
): WorkflowInstanceListItem => ({
  id: dto.id,
  definitionId: dto.workflowDefinitionId,
  definitionName: dto.definitionName ?? "",
  refNo: dto.refNo ?? "",
  title: dto.title || dto.definitionName || PLACEHOLDER,
  subtitle: dto.subtitle || dto.definitionName || "",
  currentStageId: dto.currentStageId === null ? null : String(dto.currentStageId),
  // A terminal case has no current stage; the badge says how it ended.
  currentStageName: dto.currentStageName || PLACEHOLDER,
  currentStageType: dto.currentStageType,
  status: toWorkflowInstanceStatus(dto.displayStatus),
  owner: dto.ownerName || PLACEHOLDER,
  age: formatWorkflowCaseAge(dto.startedOn, dto.completedOn, now),
});

export const toWorkflowMyWorkItem = (dto: WorkflowMyWorkItemDto): WorkflowMyWorkItem => ({
  // A stage can hold an approval and several tasks for one case, so the kind and task are both in the key.
  id: `${dto.kind}-${dto.instanceId}-${dto.stageId}-${dto.taskId ?? 0}`,
  instanceId: dto.instanceId,
  definitionId: dto.workflowDefinitionId,
  stageId: String(dto.stageId),
  // A task item is named by its checklist line; approvals and forms by their canvas.
  stageName: (dto.kind === "task" && dto.taskTitle) || dto.stageName || PLACEHOLDER,
  stageType: dto.stageType,
  caseTitle: dto.title || dto.definitionName || PLACEHOLDER,
  refNo: dto.refNo ?? "",
  kind: dto.kind,
  canAct: dto.canAct,
  isOverdue: dto.isOverdue,
});
